/**
 * renderedBodyCache — the transcript's rendered-HTML memo, as a leaf.
 *
 * WHY THIS EXISTS AT ALL (2026-08-23): every message body in the transcript
 * re-runs the markdown → HTML → sanitize → link-normalize pipeline on EVERY
 * render, and that pipeline runs `new DOMParser().parseFromString` several
 * times over (utils.ts). With the paced reveal repainting the live bubble up
 * to once per animation frame, a transcript re-parses its ENTIRE history at
 * 60Hz while a reply streams. The pipeline is pure in its inputs, so a memo
 * makes every settled message a lookup and leaves only the one growing bubble
 * doing real work.
 *
 * WHY IT MOVED HERE (2026-09-14): the original lived inline in
 * GlobalChatSystem.tsx and had two defects that only bite on LONG threads —
 * which is exactly what scroll-to-load-history produces:
 *
 *   1. THE CAP (300) SAT BELOW THE WORKING SET. `renderMessages()` walks the
 *      whole transcript every render, so the access pattern is a cyclic scan.
 *      A cyclic scan over K distinct bodies through a cache of C < K misses
 *      EVERY TIME — under LRU exactly as much as under FIFO; it is the
 *      textbook worst case for both. So the cap, not the policy, is the fix,
 *      and the cap has to clear the largest transcript we will actually
 *      render. Stated plainly because it is easy to read the LRU change below
 *      as the repair and then "tune" the cap back down.
 *   2. It covered only bodies WITHOUT stored html. Stored-html bubbles ran
 *      the pipeline inline on every commit, uncached — so the 60Hz re-parse
 *      the memo was written to prevent still happened for every one of them.
 *
 * The policy is LRU anyway, because it is right for every access pattern that
 * is NOT a full cyclic scan — a reader parked on one part of a thread, a
 * streaming bubble touched 60 times a second, the bounded render window this
 * cache is sized to outlive. Under the old FIFO an entry being read on every
 * single frame still aged out on schedule while untouched neighbours stayed.
 *
 * Bounded on TWO axes, because the axes fail differently. Entry count bounds
 * an ordinary long thread; total characters bounds the case a count cannot see
 * — a handful of very large bodies (document cards, long tables) retaining
 * megabytes. A value larger than the whole character budget is never stored,
 * so one enormous body can never evict the entire cache to make room for
 * itself.
 *
 * Leaf module: no React, no DOM, no component imports, so the eviction
 * policy is pinnable without rendering the chat surface.
 */

/** Entries retained. Sits above the largest realistic transcript: the open
 *  window is 100 messages and auto-loaded history adds 60 per page up to the
 *  per-thread page cap, so ~700 is the practical ceiling. */
export const RENDERED_BODY_CACHE_MAX_ENTRIES = 1_200;

/** Characters of rendered HTML retained, across all entries. */
export const RENDERED_BODY_CACHE_MAX_CHARS = 2_000_000;

export interface RenderedBodyCache {
  /** Returns the memo for `key`, refreshing its recency on a hit. */
  get(key: string): string | undefined;
  /** Stores `html` under `key`, evicting least-recently-used entries as needed. */
  set(key: string, html: string): void;
  /** Entries currently retained (tests + diagnostics). */
  readonly size: number;
  /** Characters currently retained (tests + diagnostics). */
  readonly chars: number;
}

/**
 * A least-recently-used cache over strings.
 *
 * Map iteration order is insertion order, so `delete` + re-`set` on a hit
 * moves a key to the young end and `keys().next().value` is then genuinely
 * the least-recently-USED entry rather than the oldest-inserted one. That one
 * line is the whole difference between this and what it replaces.
 */
export function createRenderedBodyCache(
  maxEntries: number = RENDERED_BODY_CACHE_MAX_ENTRIES,
  maxChars: number = RENDERED_BODY_CACHE_MAX_CHARS,
): RenderedBodyCache {
  const entries = new Map<string, string>();
  let chars = 0;

  const evictOldest = (): boolean => {
    const oldest = entries.keys().next().value;
    if (oldest === undefined) return false;
    const evicted = entries.get(oldest);
    entries.delete(oldest);
    chars -= evicted ? evicted.length : 0;
    return true;
  };

  return {
    get(key: string): string | undefined {
      const hit = entries.get(key);
      if (hit === undefined) return undefined;
      // Refresh recency: this is what makes the policy LRU rather than FIFO.
      entries.delete(key);
      entries.set(key, hit);
      return hit;
    },

    set(key: string, html: string): void {
      const existing = entries.get(key);
      if (existing !== undefined) {
        entries.delete(key);
        chars -= existing.length;
      }
      // A body that cannot fit the budget alone is never stored — otherwise
      // it would evict every other entry and then still not fit.
      if (html.length > maxChars) return;
      entries.set(key, html);
      chars += html.length;
      while ((entries.size > maxEntries || chars > maxChars) && entries.size > 1) {
        if (!evictOldest()) break;
      }
    },

    get size(): number {
      return entries.size;
    },

    get chars(): number {
      return chars;
    },
  };
}
